import { useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export default function TermsPage() {
  useEffect(() => { document.title = 'Användarvillkor | H.J.Ä.L.P.'; }, []);
  return (
    <div className="py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <h1 className="text-3xl font-bold tracking-tight">Användarvillkor</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Senast uppdaterad: {new Date().toLocaleDateString('sv-SE')}
            </p>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none space-y-6">
            <section className="bg-muted/50 rounded-lg p-4 border-l-4 border-primary">
              <p className="text-sm font-medium">
                H.J.Ä.L.P. är ett pedagogiskt stödprogram — inte hälso- och sjukvård.
                Plattformen ersätter inte professionell psykologisk eller medicinsk vård.
                Vid akut kris: ring 112 eller Minds självmordsline 90000.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Inledning</h2>
              <p>
                Välkommen till H.J.Ä.L.P., ett digitalt KBT-baserat rehabiliteringsprogram
                för personlig förändring och återintegration. Genom att använda denna tjänst godkänner du dessa användarvillkor.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Syfte och Användning</h2>
              <p className="mb-2">
                Denna plattform tillhandahåller:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Riskbedömningar baserade på evidensbaserade metoder</li>
                <li>Personaliserade KBT-program med 48 sessioner</li>
                <li>Framstegsspårning och dokumentation</li>
                <li>Kommunikation mellan klienter och handledare</li>
              </ul>
              <p className="mt-2">
                Plattformen är ett pedagogiskt stödprogram och ska användas
                under handledning av behörig personal. Den ersätter inte hälso- och sjukvård.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Användarkonton och Roller</h2>
              <p className="mb-2">
                Tre användarroller finns:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><strong>Klient:</strong> Genomför bedömningar och sessioner</li>
                <li><strong>Handledare:</strong> Utför bedömningar och följer klienters framsteg</li>
                <li><strong>Administratör:</strong> Hanterar systemet och användarrättigheter</li>
              </ul>
              <p className="mt-2">
                Du ansvarar för att hålla dina inloggningsuppgifter konfidentiella och för all aktivitet
                som sker under ditt konto.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Acceptabel Användning</h2>
              <p className="mb-2">
                Du förbinder dig att:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Använda plattformen endast för avsett rehabiliteringsändamål</li>
                <li>Tillhandahålla sanningsenlig information i bedömningar</li>
                <li>Respektera andra användares integritet och konfidentialitet</li>
                <li>Inte försöka få obehörig åtkomst till systemet eller andras data</li>
                <li>Inte använda plattformen för olagliga ändamål</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Behandling av Personuppgifter</h2>
              <p>
                All behandling av personuppgifter sker i enlighet med GDPR och vår Integritetspolicy.
                Genom att använda plattformen samtycker du till behandling av dina personuppgifter enligt
                beskrivningen i Integritetspolicyn.
              </p>
              <p className="mt-2">
                Bedömningsdata och sessionsinformation lagras krypterat och är endast tillgänglig för
                behörig personal inom programmet.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Handledarens Ansvar</h2>
              <p>
                Handledare som använder plattformen ansvarar för:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Korrekt genomförande av bedömningar enligt riktlinjer</li>
                <li>Professionell hantering av klientinformation</li>
                <li>Regelbunden uppföljning av tilldelade klienter</li>
                <li>Rapportering av eventuella säkerhetsincidenter</li>
                <li>Efterlevnad av etiska riktlinjer för programmet</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Immateriella Rättigheter</h2>
              <p>
                Allt innehåll på plattformen, inklusive texter, bilder, övningar och bedömningsverktyg,
                ägs av eller licensieras till operatören av H.J.Ä.L.P. Du får inte kopiera, distribuera eller
                skapa bearbetningar av innehållet utan skriftligt tillstånd.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Tjänstens Tillgänglighet</h2>
              <p>
                Vi strävar efter att hålla plattformen tillgänglig dygnet runt, men garanterar inte
                oavbruten åtkomst. Tjänsten kan tillfälligt vara otillgänglig för underhåll, uppgraderingar
                eller tekniska problem.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Ansvarsbegränsning</h2>
              <p>
                Plattformen tillhandahålls "som den är". Vi ansvarar inte för:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Direkta eller indirekta skador till följd av användning av plattformen</li>
                <li>Förlust av data på grund av tekniska fel</li>
                <li>Beslut fattade baserat på bedömningsresultat (professionell bedömning krävs alltid)</li>
                <li>Obehörig åtkomst till system eller data till följd av användarens vårdslöshet</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Ändringar av Villkoren</h2>
              <p>
                Vi förbehåller oss rätten att när som helst ändra dessa användarvillkor. Väsentliga
                ändringar kommer att kommuniceras via plattformen. Fortsatt användning efter ändringar
                innebär acceptans av de nya villkoren.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Uppsägning</h2>
              <p>
                Vi förbehåller oss rätten att avsluta eller begränsa din åtkomst till plattformen om:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Du bryter mot dessa användarvillkor</li>
                <li>Din koppling till programmet upphör</li>
                <li>Det finns misstanke om missbruk av systemet</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Tillämplig Lag</h2>
              <p>
                Dessa användarvillkor ska tolkas och tillämpas i enlighet med svensk lag.
                Eventuella tvister ska avgöras av svensk domstol.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Kontaktinformation</h2>
              <p>
                För frågor om dessa användarvillkor, kontakta:
              </p>
              <p className="mt-2">
                <strong>Operatören av H.J.Ä.L.P.</strong><br />
                Webbplats: sinnerjin.com<br />
                Kontakt: Via kontaktformuläret på sinnerjin.com
              </p>
            </section>

            <section className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground">
                Genom att klicka på "Jag accepterar" vid registrering bekräftar du att du har läst,
                förstått och accepterat dessa användarvillkor.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
